import { useMemo } from "react";
import { motion } from "framer-motion";
import { Droplets, Plus, Minus, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { playDrop } from "@/lib/fx";

const GLASS_ML = 250;

export default function WaterView({ entry, setEntry, profile, entries, todayKey }) {
  const goal = profile?.water_goal || 10;
  const current = entry?.water || 0;
  const pct = Math.min(1, current / goal);

  const set = (n, sound = false) => {
    const next = Math.max(0, Math.min(20, n));
    if (sound && next > (entry?.water || 0)) playDrop();
    setEntry({ ...entry, water: next });
  };

  const updateGoal = async (delta) => {
    const next = Math.max(1, Math.min(20, goal + delta));
    try {
      await api.updateProfile({ water_goal: next });
      window.dispatchEvent(new CustomEvent("profile-updated", { detail: { water_goal: next } }));
    } catch {}
  };

  const weekStats = useMemo(() => {
    // last 7 days including today
    const today = new Date(todayKey);
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const e = k === todayKey ? entry : entries.find((x) => x.date === k);
      days.push({ key: k, water: e?.water || 0, label: d.toLocaleDateString(undefined, { weekday: "short" })[0] });
    }
    return days;
  }, [entries, entry, todayKey]);

  const avg = weekStats.reduce((a, b) => a + b.water, 0) / 7;

  return (
    <div className="px-6 pt-8 pb-10" data-testid="water-view">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#6B7A70]">
        Hydration
      </div>
      <h2 className="font-heading text-3xl font-semibold text-[#2A332C] tracking-tight mt-1">
        Drink to your skin.
      </h2>
      <p className="text-sm text-[#6B7A70] mt-2">
        Isotane dries everything. Sip steadily — your skin barrier will thank you.
      </p>

      {/* Big glass visual */}
      <div className="mt-6 relative mx-auto" style={{ width: 200, height: 260 }}>
        <svg viewBox="0 0 200 260" className="w-full h-full">
          {/* Glass outline */}
          <path
            d="M40 30 L160 30 L150 240 Q150 250 140 250 L60 250 Q50 250 50 240 Z"
            fill="none"
            stroke="#4A6B53"
            strokeWidth="3"
            strokeLinejoin="round"
          />
          {/* Water clip */}
          <defs>
            <clipPath id="glassClip">
              <path d="M42 32 L158 32 L148 238 Q148 248 138 248 L62 248 Q52 248 52 238 Z" />
            </clipPath>
          </defs>
          <g clipPath="url(#glassClip)">
            <motion.rect
              x="0"
              initial={{ y: 250 }}
              animate={{ y: 250 - pct * 218 }}
              transition={{ type: "spring", stiffness: 60, damping: 18 }}
              width="200"
              height="260"
              fill="url(#waterGrad)"
            />
            {/* Wave */}
            <motion.path
              animate={{ x: [-20, 0, -20] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              d={`M-20 ${250 - pct * 218} q 25 -8 50 0 t 50 0 t 50 0 t 50 0 t 50 0 V 260 H -20 Z`}
              fill="#7BB9D9"
              opacity="0.7"
            />
          </g>
          <defs>
            <linearGradient id="waterGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#A8D5EC" />
              <stop offset="1" stopColor="#5C9DBF" />
            </linearGradient>
          </defs>
        </svg>
        {/* Glass text — color flips to white once water passes the text centre */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          style={{
            color: pct > 0.52 ? "#fff" : "#2A332C",
            transition: "color 0.4s ease",
            textShadow: pct > 0.3 ? "0 1px 4px rgba(0,0,0,0.18)" : "none",
          }}
        >
          <motion.div
            key={current}
            initial={{ scale: 1.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="font-heading text-6xl font-semibold leading-none"
            data-testid="water-count"
          >
            {current}
          </motion.div>
          <div
            className="text-[11px] font-bold uppercase tracking-[0.2em] mt-1.5"
            style={{ opacity: pct > 0.52 ? 0.85 : 0.55 }}
          >
            of {goal} glasses
          </div>
          <div
            className="text-[11px] mt-0.5"
            style={{ opacity: pct > 0.52 ? 0.7 : 0.45 }}
          >
            {current * GLASS_ML} ml
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex items-center justify-center gap-4">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => set(current - 1)}
          data-testid="water-minus"
          className="w-12 h-12 rounded-full bg-white border border-[#E9EFEA] flex items-center justify-center"
        >
          <Minus className="w-5 h-5 text-[#2A332C]" strokeWidth={1.5} />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={() => set(current + 1, true)}
          data-testid="water-plus"
          className="px-6 h-14 rounded-full bg-[#2A332C] text-white font-medium flex items-center gap-2 shadow-lg"
        >
          <Droplets className="w-5 h-5" strokeWidth={1.5} />
          Add a glass
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => set(0)}
          data-testid="water-reset"
          className="w-12 h-12 rounded-full bg-white border border-[#E9EFEA] flex items-center justify-center"
        >
          <RotateCcw className="w-4 h-4 text-[#6B7A70]" strokeWidth={1.5} />
        </motion.button>
      </div>

      {/* Week stats */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-base font-medium text-[#2A332C]">This week</h3>
          <span className="text-xs text-[#6B7A70]">
            avg {avg.toFixed(1)} / day
          </span>
        </div>
        <div className="bg-white rounded-3xl p-4 border border-[#E9EFEA]">
          <div className="flex items-end justify-between h-32 gap-2">
            {weekStats.map((d, i) => {
              const h = Math.min(1, d.water / goal) * 100;
              const isToday = d.key === todayKey;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <div className="relative w-full h-24 flex items-end">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      className={`w-full rounded-t-lg ${
                        isToday ? "bg-[#5C9DBF]" : "bg-[#A8D5EC]"
                      }`}
                    />
                  </div>
                  <span className={`text-[10px] font-medium ${isToday ? "text-[#2A332C]" : "text-[#6B7A70]"}`}>
                    {d.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Goal editor */}
      <div className="mt-6 p-4 bg-white rounded-2xl border border-[#E9EFEA] flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#E9F0E8] flex items-center justify-center shrink-0">
          <Droplets className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="font-heading font-medium text-[#2A332C]">Daily goal</div>
          <div className="text-xs text-[#6B7A70]">{goal} × 250 ml glasses</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="goal-minus"
            onClick={() => updateGoal(-1)}
            className="w-8 h-8 rounded-full bg-[#F7F5F0] flex items-center justify-center"
          >
            <Minus className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          <span className="w-8 text-center font-semibold text-sm">{goal}</span>
          <button
            data-testid="goal-plus"
            onClick={() => updateGoal(1)}
            className="w-8 h-8 rounded-full bg-[#F7F5F0] flex items-center justify-center"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
